/**
 * InCallModal — full on-call surface.
 *
 * Auto-opens when the softphone transitions to a connected (or outbound-
 * dialing) call. Renders a floating two-column panel:
 *
 *   • Left  — contact identity (name, phone, brand, duration), a recording
 *             indicator, and the call-control menu: mute, hold, transfer,
 *             a DTMF keypad, and end-call. Same canonical actions the
 *             compact `ActiveCallWidget` strip exposes plus the keypad
 *             needed for IVR navigation.
 *   • Right — `CallScriptPanel` with admin-authored scripts resolved
 *             server-side. No placeholder/default content.
 *
 * UX notes
 *   - **No backdrop**: the page behind stays clickable and visible. The
 *     rep often needs to read contact panels / lookup info while on a
 *     call, and dimming the screen would defeat that.
 *   - **Draggable** by the header (pointer events, no library) and
 *     **resizable** from the bottom-right corner via native CSS. Both
 *     position and size persist per browser via localStorage and clamp
 *     to the current viewport on each open so a stale value can't
 *     render the panel off-screen.
 *   - **z-index** sits below transient modals (TransferDialog, etc.) so
 *     they pop over the on-call surface when triggered from inside it.
 *
 * Lifecycle
 *   - opens on `apex:call-update` when phase becomes 'connected' or
 *     outbound 'dialing'/'ringing'.
 *   - closes when phase becomes 'ended' (or `apex:call-ended` fires).
 *   - manual minimize: the X button hides the modal but leaves the call
 *     active. The compact `ActiveCallWidget` in the page header remains
 *     visible; clicking the contact name / Expand button there
 *     dispatches `apex:incall-modal-show` to bring the modal back.
 *
 * Inbound ringing is handled by `InboundCallToast` — this modal stays
 * out of the way until the rep answers.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowRightLeft,
  ChevronDown,
  Circle,
  Grid3x3,
  Mic,
  MicOff,
  Minimize2,
  Pause,
  Phone,
  PhoneOff,
  Play,
  Users,
} from 'lucide-react';
import { CallScriptPanel } from '../shared/CallScriptPanel';
import { BrandBadge } from '../shared/BrandBadge';
import { apiGet } from '../../hooks/useApi';

interface ActiveCall {
  callId: string;
  phase: 'dialing' | 'ringing' | 'connected' | 'rejoining' | 'ended';
  direction: 'inbound' | 'outbound';
  from: string;
  to: string;
  contactName?: string;
  contactId?: string;
  contactSelected?: boolean;
  brand?: string;
  held: boolean;
  muted: boolean;
  transfer: { type: 'cold' | 'warm'; to: string; name?: string } | null;
  startedAt: number;
  answeredAt?: number;
}

interface ContactMatch {
  id: string;
  name: string | null;
  agencyName: string | null;
}

interface Softphone {
  hangup: () => void;
  toggleMute: () => void;
  toggleHold: () => void;
  showTransferDialog: () => void;
  sendDtmf: (digits: string) => Promise<void>;
  completeWarmTransfer: () => Promise<void>;
  cancelTransfer: () => Promise<void>;
  getActiveCall: () => ActiveCall | null;
  getRecordingState: () => 'idle' | 'recording' | 'paused' | 'stopped';
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<void>;
  setCallContact: (contactId: string, contactName: string) => void;
}

function getSoftphone(): Softphone | null {
  return (window as unknown as Record<string, unknown>)['ApexSoftphone'] as Softphone | null;
}

// ── Persisted geometry ───────────────────────────────────────────────────

const STORAGE_KEY = 'apex.incall-modal.geometry';
const DEFAULT_WIDTH = 1100;
const DEFAULT_HEIGHT = 750;
const MIN_WIDTH = 720;
const MIN_HEIGHT = 480;

interface Geometry {
  x: number;
  y: number;
  width: number;
  height: number;
}

function clampGeometry(g: Geometry): Geometry {
  if (typeof window === 'undefined') return g;
  const maxW = window.innerWidth - 16;
  const maxH = window.innerHeight - 16;
  const width = Math.min(maxW, Math.max(MIN_WIDTH, g.width));
  const height = Math.min(maxH, Math.max(MIN_HEIGHT, g.height));
  // Keep at least 80px of the modal on-screen so a buggy stored x/y
  // can't render it off the visible viewport.
  const x = Math.max(80 - width, Math.min(window.innerWidth - 80, g.x));
  const y = Math.max(0, Math.min(window.innerHeight - 80, g.y));
  return { x, y, width, height };
}

function defaultGeometry(): Geometry {
  if (typeof window === 'undefined') {
    return { x: 0, y: 0, width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT };
  }
  const w = Math.min(DEFAULT_WIDTH, window.innerWidth - 32);
  const h = Math.min(DEFAULT_HEIGHT, window.innerHeight - 32);
  return {
    x: Math.max(0, Math.round((window.innerWidth - w) / 2)),
    y: Math.max(0, Math.round((window.innerHeight - h) / 2)),
    width: w,
    height: h,
  };
}

function loadGeometry(): Geometry {
  if (typeof window === 'undefined') return defaultGeometry();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultGeometry();
    const parsed = JSON.parse(raw) as Partial<Geometry>;
    const x = Number(parsed.x);
    const y = Number(parsed.y);
    const w = Number(parsed.width);
    const h = Number(parsed.height);
    if (![x, y, w, h].every(Number.isFinite)) return defaultGeometry();
    return clampGeometry({ x, y, width: w, height: h });
  } catch {
    return defaultGeometry();
  }
}

function saveGeometry(g: Geometry) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(g));
  } catch {
    // Quota or privacy-mode failure — geometry just won't persist this session.
  }
}

// ── Helpers ──────────────────────────────────────────────────────────────

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** Elapsed-time readout. Owns the 1s tick so only it re-renders per second. */
function CallDuration({ answeredAt }: { answeredAt: number }) {
  const [elapsed, setElapsed] = useState(() => Math.floor((Date.now() - answeredAt) / 1000));
  useEffect(() => {
    const tick = () => setElapsed(Math.floor((Date.now() - answeredAt) / 1000));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [answeredAt]);
  return <>{formatDuration(elapsed)}</>;
}

const KEYPAD_DIGITS: Array<{ digit: string; sub?: string }> = [
  { digit: '1' },
  { digit: '2', sub: 'ABC' },
  { digit: '3', sub: 'DEF' },
  { digit: '4', sub: 'GHI' },
  { digit: '5', sub: 'JKL' },
  { digit: '6', sub: 'MNO' },
  { digit: '7', sub: 'PQRS' },
  { digit: '8', sub: 'TUV' },
  { digit: '9', sub: 'WXYZ' },
  { digit: '*' },
  { digit: '0', sub: '+' },
  { digit: '#' },
];

// ── Component ────────────────────────────────────────────────────────────

export function InCallModal() {
  const [call, setCall] = useState<ActiveCall | null>(null);
  const [recording, setRecording] = useState<string>('idle');
  const [minimized, setMinimized] = useState(false);
  const [geometry, setGeometry] = useState<Geometry>(() => loadGeometry());
  // Keypad is open on every fresh call — most reps want it visible by
  // default for IVR navigation. Toggling it closed only collapses for the
  // current call; reopening a new call resets to expanded.
  const [keypadOpen, setKeypadOpen] = useState(true);
  const [pressedDigits, setPressedDigits] = useState<string>('');
  // Contacts sharing the caller's number, for the in-call "who's calling?"
  // picker shown when the number is ambiguous.
  const [contactMatches, setContactMatches] = useState<{ count: number; contacts: ContactMatch[] }>(
    { count: 0, contacts: [] },
  );
  const [pickerOpen, setPickerOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  // Track an active drag without forcing re-renders on every pointermove.
  // Stored: pointer offset relative to the panel's top-left so dragging
  // feels anchored at wherever the rep grabbed the header.
  const dragRef = useRef<{
    pointerId: number;
    offsetX: number;
    offsetY: number;
  } | null>(null);
  // Mirror of `geometry` in a ref so the resize observer's debounced
  // persist can read the latest value without re-entering setGeometry.
  const geometryRef = useRef(geometry);
  useEffect(() => {
    geometryRef.current = geometry;
  }, [geometry]);

  // Subscribe to softphone DOM events. Same event surface the
  // ActiveCallWidget uses — keeping them in lockstep.
  useEffect(() => {
    const handleUpdate = (e: Event) => {
      const detail = (e as CustomEvent).detail as ActiveCall | null;
      // Auto-open when a fresh call appears so the rep doesn't have to
      // hunt for the panel — the explicit `apex:incall-modal-show` event
      // handles re-popping after a manual minimize.
      setCall((prev) => {
        if (!prev && detail) setMinimized(false);
        return detail;
      });
      const sp = getSoftphone();
      if (sp) setRecording(sp.getRecordingState());
    };
    const handleEnded = () => {
      setCall(null);
      setRecording('idle');
      setMinimized(false);
      // Keypad resets to OPEN for the next call, matching the initial-state
      // default. Pressed digits clear so the buffer doesn't survive across
      // calls.
      setKeypadOpen(true);
      setPressedDigits('');
    };
    const handleShow = () => setMinimized(false);

    window.addEventListener('apex:call-update', handleUpdate);
    window.addEventListener('apex:call-ended', handleEnded);
    window.addEventListener('apex:incall-modal-show', handleShow);

    // Recover state if mounted mid-call.
    const sp = getSoftphone();
    if (sp) {
      const existing = sp.getActiveCall();
      if (existing) {
        setCall(existing);
        setRecording(sp.getRecordingState());
      }
    }

    return () => {
      window.removeEventListener('apex:call-update', handleUpdate);
      window.removeEventListener('apex:call-ended', handleEnded);
      window.removeEventListener('apex:incall-modal-show', handleShow);
    };
  }, []);

  // Resolve how many contacts share the caller's number, for the header
  // "who's calling?" picker. Same agency-scoped endpoint the ring toast uses.
  // Best-effort — no picker if the lookup fails.
  const lookupPhone = call ? (call.direction === 'inbound' ? call.from : call.to) : '';
  useEffect(() => {
    if (!lookupPhone) {
      setContactMatches({ count: 0, contacts: [] });
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await apiGet<{ count: number; contacts: ContactMatch[] }>(
          `/api/contacts/by-phone?phone=${encodeURIComponent(lookupPhone)}`,
        );
        if (!cancelled) {
          setContactMatches({ count: res.count ?? 0, contacts: res.contacts ?? [] });
        }
      } catch {
        // Best-effort — leave matches empty so no picker shows.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lookupPhone]);

  // Persist size whenever the user drags the resize handle.
  //
  // Two subtle bugs the prior version had, both fixed here:
  //
  //   1. We have to read `borderBoxSize` (or `getBoundingClientRect`),
  //      NOT `contentRect`. Tailwind defaults to `box-sizing: border-box`,
  //      so the inline style we set is the BORDER box width. ResizeObserver
  //      `contentRect` is the CONTENT box (smaller by 2× border + padding).
  //      Mixing the two creates a feedback loop: each render shrinks the
  //      observed size by ~2px, the observer fires, state shrinks, we
  //      render even smaller, … modal collapses to a single pixel.
  //
  //   2. `saveGeometry` was called inside the `setGeometry` updater, which
  //      runs during render. Even though the side-effect is "safe" (just
  //      localStorage), it's clearer to write it after the state commit.
  //      Tracking the latest geometry in a ref lets us persist on a
  //      debounced rAF without re-entering the setter.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof window === 'undefined') return;
    let saveScheduled = 0;
    const obs = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      // Prefer borderBoxSize (matches what we set in style with
      // box-sizing: border-box). Fallback to getBoundingClientRect for
      // browsers without borderBoxSize support.
      const box = entry.borderBoxSize?.[0];
      const width = box ? box.inlineSize : el.getBoundingClientRect().width;
      const height = box ? box.blockSize : el.getBoundingClientRect().height;
      setGeometry((prev) => {
        // Tolerance of 1px so sub-pixel rounding can't trigger updates.
        // The observer-and-render cycle has rounded both sides anyway;
        // anything within 1px is the same size for the rep.
        if (Math.abs(prev.width - width) <= 1 && Math.abs(prev.height - height) <= 1) {
          return prev;
        }
        return { ...prev, width: Math.round(width), height: Math.round(height) };
      });
      // Debounced persist — coalesce rapid resize events into one write.
      if (saveScheduled) cancelAnimationFrame(saveScheduled);
      saveScheduled = requestAnimationFrame(() => {
        saveScheduled = 0;
        saveGeometry(geometryRef.current);
      });
    });
    obs.observe(el);
    return () => {
      obs.disconnect();
      if (saveScheduled) cancelAnimationFrame(saveScheduled);
    };
  }, [call, minimized]);

  // Drag handlers — pointer events on the header.
  const onHeaderPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    // Don't initiate a drag from interactive elements inside the header.
    if ((e.target as HTMLElement).closest('button')) return;
    const rect = (e.currentTarget.parentElement as HTMLElement | null)?.getBoundingClientRect();
    if (!rect) return;
    dragRef.current = {
      pointerId: e.pointerId,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  const onHeaderPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    setGeometry((prev) =>
      clampGeometry({
        ...prev,
        x: e.clientX - d.offsetX,
        y: e.clientY - d.offsetY,
      }),
    );
  }, []);

  const onHeaderPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    dragRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // ignore — capture may have already been released by the browser
    }
    setGeometry((prev) => {
      saveGeometry(prev);
      return prev;
    });
  }, []);

  const hangup = useCallback(() => getSoftphone()?.hangup(), []);
  const toggleMute = useCallback(() => getSoftphone()?.toggleMute(), []);
  const toggleHold = useCallback(() => getSoftphone()?.toggleHold(), []);
  const transfer = useCallback(() => getSoftphone()?.showTransferDialog(), []);
  const completeWarmTransfer = useCallback(() => {
    void getSoftphone()
      ?.completeWarmTransfer()
      .catch(() => {});
  }, []);
  const cancelTransfer = useCallback(() => {
    void getSoftphone()
      ?.cancelTransfer()
      .catch(() => {});
  }, []);
  const pressDigit = useCallback((digit: string) => {
    void getSoftphone()
      ?.sendDtmf(digit)
      .catch(() => {});
    setPressedDigits((prev) => (prev + digit).slice(-12));
  }, []);

  // Hide for: no call, ended call, minimized, or inbound ringing
  // (InboundCallToast owns the answer/reject UI for incoming).
  if (!call) return null;
  if (call.phase === 'ended') return null;
  if (minimized) return null;
  if (call.phase === 'ringing' && call.direction === 'inbound') return null;

  const displayName =
    call.contactName || (call.direction === 'inbound' ? call.from : call.to) || 'Unknown caller';
  const phoneNumber = call.direction === 'inbound' ? call.from : call.to;
  // Offer the duplicate picker only when the number is genuinely ambiguous
  // and the rep hasn't already confirmed who's calling.
  const showPicker = contactMatches.count > 1 && !call.contactSelected;
  const isConnected = call.phase === 'connected';
  const isDialing =
    call.phase === 'dialing' || (call.phase === 'ringing' && call.direction === 'outbound');

  return (
    <div
      ref={containerRef}
      // No backdrop — the page behind stays interactive. z-30 sits above
      // page chrome (sidebar/header) but below transient modals
      // (TransferDialog at z-50) so they pop over this surface when
      // triggered from inside it.
      style={{
        position: 'fixed',
        left: geometry.x,
        top: geometry.y,
        width: geometry.width,
        height: geometry.height,
        zIndex: 30,
      }}
      className="bg-card border border-border/60 rounded-2xl shadow-2xl flex flex-col overflow-hidden resize"
      role="dialog"
      aria-label="On call"
    >
      {/* Header — drag handle. Pointer events on the wrapping div so the
          rep can grab anywhere except buttons. */}
      <div
        onPointerDown={onHeaderPointerDown}
        onPointerMove={onHeaderPointerMove}
        onPointerUp={onHeaderPointerUp}
        onPointerCancel={onHeaderPointerUp}
        className="px-6 py-4 border-b border-border/60 flex items-center justify-between flex-shrink-0 select-none cursor-move"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
              call.held
                ? 'bg-amber-500/10 text-amber-600'
                : isDialing
                  ? 'bg-amber-500/10 text-amber-600 animate-pulse'
                  : 'bg-brand-green/10 text-brand-green'
            }`}
          >
            <Phone className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h2 className="text-[1rem] font-semibold text-foreground truncate flex items-center gap-2">
              <span className="truncate">{displayName}</span>
              {call.brand && <BrandBadge brand={call.brand} size="xs" className="flex-shrink-0" />}
            </h2>
            <div className="text-[0.75rem] text-muted-foreground flex items-center gap-2 mt-0.5">
              {recording === 'recording' && (
                <Circle className="w-2 h-2 fill-destructive text-destructive animate-pulse flex-shrink-0" />
              )}
              <span className="font-mono">{phoneNumber}</span>
              <span>·</span>
              <span className="tabular-nums">
                {call.phase === 'rejoining' ? (
                  'Reconnecting…'
                ) : isDialing ? (
                  'Dialing…'
                ) : call.held ? (
                  'On Hold'
                ) : call.answeredAt ? (
                  <CallDuration answeredAt={call.answeredAt} />
                ) : (
                  '0:00'
                )}
              </span>
            </div>
          </div>
        </div>
        <button
          onClick={() => setMinimized(true)}
          className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-secondary transition-colors cursor-pointer flex-shrink-0"
          title="Minimize — call stays active"
          aria-label="Minimize"
        >
          <Minimize2 className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>

      {/* Duplicate-caller picker. The number maps to several contacts; after
          the rep asks "who's this?", picking one sets the call's contact
          across the modal + widget. Display-only — not persisted to the CDR. */}
      {showPicker && (
        <div className="px-6 py-2 border-b border-border/60 bg-amber-500/5 flex-shrink-0">
          <div className="relative">
            <button
              type="button"
              onClick={() => setPickerOpen((o) => !o)}
              aria-label="Select which contact is calling"
              className="w-full flex items-center justify-between gap-2 text-left px-3 py-2 rounded-lg border border-amber-500/30 bg-card hover:bg-secondary/40 transition-colors cursor-pointer"
            >
              <span className="flex items-center gap-2 min-w-0">
                <Users className="w-4 h-4 text-amber-600 flex-shrink-0" />
                <span className="text-[0.8125rem] text-foreground truncate">
                  {contactMatches.count} contacts share this number — who's calling?
                </span>
              </span>
              <ChevronDown
                className={`w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform ${
                  pickerOpen ? 'rotate-180' : ''
                }`}
              />
            </button>
            {pickerOpen && (
              <div className="absolute z-10 left-0 right-0 mt-1 max-h-56 overflow-y-auto rounded-lg border border-border/60 bg-card shadow-xl divide-y divide-border/40">
                {contactMatches.contacts.map((cand) => (
                  <button
                    key={cand.id}
                    type="button"
                    onClick={() => {
                      getSoftphone()?.setCallContact(cand.id, cand.name ?? '');
                      setPickerOpen(false);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-secondary/40 transition-colors cursor-pointer"
                  >
                    <span className="block text-[0.8125rem] font-medium text-foreground truncate">
                      {cand.name || 'Unnamed contact'}
                    </span>
                    {cand.agencyName && (
                      <span className="block text-[0.6875rem] text-muted-foreground truncate">
                        {cand.agencyName}
                      </span>
                    )}
                  </button>
                ))}
                {contactMatches.count > contactMatches.contacts.length && (
                  <p className="px-3 py-2 text-[0.6875rem] text-muted-foreground">
                    + {contactMatches.count - contactMatches.contacts.length} more — search Contacts
                    to narrow
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Body — two columns */}
      <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* Left: call control menu */}
        <div className="w-[260px] flex-shrink-0 border-r border-border/60 bg-secondary/20 p-5 flex flex-col gap-3 overflow-y-auto">
          <button
            onClick={toggleMute}
            disabled={!isConnected}
            className={`w-full px-4 py-3 rounded-xl text-[0.8125rem] font-semibold border transition-colors cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
              call.muted
                ? 'bg-destructive/10 text-destructive border-destructive/30'
                : 'bg-card text-foreground border-border/60 hover:bg-secondary'
            }`}
          >
            {call.muted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            {call.muted ? 'Unmute' : 'Mute'}
          </button>
          <button
            onClick={toggleHold}
            disabled={!isConnected}
            className={`w-full px-4 py-3 rounded-xl text-[0.8125rem] font-semibold border transition-colors cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
              call.held
                ? 'bg-amber-500/10 text-amber-600 border-amber-500/30'
                : 'bg-card text-foreground border-border/60 hover:bg-secondary'
            }`}
          >
            {call.held ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
            {call.held ? 'Resume' : 'Hold'}
          </button>
          <button
            onClick={transfer}
            disabled={!isConnected || !!call.transfer}
            className="w-full px-4 py-3 rounded-xl text-[0.8125rem] font-semibold border border-border/60 bg-card text-foreground hover:bg-secondary transition-colors cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowRightLeft className="w-4 h-4" />
            Transfer
          </button>
          <button
            onClick={() => setKeypadOpen((v) => !v)}
            disabled={!isConnected}
            className={`w-full px-4 py-3 rounded-xl text-[0.8125rem] font-semibold border transition-colors cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
              keypadOpen
                ? 'bg-primary/10 text-primary border-primary/30'
                : 'bg-card text-foreground border-border/60 hover:bg-secondary'
            }`}
            title="DTMF keypad — send tones for IVR menus"
          >
            <Grid3x3 className="w-4 h-4" />
            Keypad
          </button>

          {call.transfer && (
            <div className="rounded-xl bg-amber-500/10 border border-amber-500/20 px-3 py-2 text-[0.6875rem] text-amber-700">
              <span className="font-bold uppercase">{call.transfer.type}</span> transfer in progress
              {call.transfer.name && <div className="mt-0.5 truncate">→ {call.transfer.name}</div>}
              {/* Warm-transfer controls. Complete bridges contact + target;
                  rep leaves. Cancel aborts and returns rep to contact. Cold
                  transfers don't surface controls here — the source rep is
                  already gone at cold-initiate. */}
              {call.transfer.type === 'warm' && (
                <div className="mt-2 flex gap-2">
                  <button
                    onClick={completeWarmTransfer}
                    className="flex-1 px-3 py-1.5 rounded-lg text-[0.6875rem] font-semibold bg-brand-green text-white hover:bg-brand-green/90 transition-colors cursor-pointer"
                    title="Bridge the contact with the target and step out"
                  >
                    Complete
                  </button>
                  <button
                    onClick={cancelTransfer}
                    className="flex-1 px-3 py-1.5 rounded-lg text-[0.6875rem] font-semibold bg-card text-foreground border border-border/60 hover:bg-secondary transition-colors cursor-pointer"
                    title="Abort the transfer and return to the contact"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          )}

          {/* DTMF keypad — expanded by default (see keypadOpen initial
              state); the Keypad button above collapses it to save
              vertical space. Sends tones via softphone.sendDtmf. */}
          {keypadOpen && (
            <div className="mt-1 rounded-xl border border-border/60 bg-card p-2 flex flex-col gap-2">
              <div className="text-[0.625rem] font-mono text-muted-foreground tabular-nums min-h-[14px] truncate text-center">
                {pressedDigits || '—'}
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                {KEYPAD_DIGITS.map((k) => (
                  <button
                    key={k.digit}
                    onClick={() => pressDigit(k.digit)}
                    disabled={!isConnected}
                    className="aspect-square rounded-lg bg-secondary/40 hover:bg-secondary text-foreground transition-colors cursor-pointer flex flex-col items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="text-[1rem] font-semibold leading-none">{k.digit}</span>
                    {k.sub && (
                      <span className="text-[0.5rem] text-muted-foreground tracking-wider mt-0.5">
                        {k.sub}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex-1" />

          <button
            onClick={hangup}
            className="w-full px-4 py-3 rounded-xl text-[0.8125rem] font-semibold bg-destructive text-white hover:bg-destructive/90 transition-colors cursor-pointer flex items-center justify-center gap-2 shadow-sm"
          >
            <PhoneOff className="w-4 h-4" />
            End Call
          </button>
        </div>

        {/* Right: call script (no placeholder fallback — empty if the
            tenant hasn't authored scripts yet). */}
        <div className="flex-1 overflow-y-auto p-6 min-w-0">
          <CallScriptPanel
            contactId={call.contactId}
            contactName={displayName}
            brand={call.brand}
          />
        </div>
      </div>
    </div>
  );
}
